// REALMS' MODEL (parity with nucleus.oS Security.swift). Two objects, kept
// apart because they behave differently:
//
// - An EXPOSURE PROFILE is a relationship — you and one app, person, group or
//   delegate: the realms it draws on, the face it sees, the reach it has
//   (`composed`: consent ∩ the union of its realms' ceilings), when it lapses.
// - A PRIVACY REALM is a container of yours that many profiles point into.
//
// Reads GET /exposure-profiles, /privacy-realms and /schemas (names for the
// ids a reach is scoped to) — a group's own when you are in the group.
//
// A grant reaches a realm through its ATTACHMENTS (`ep.realms`), and can be
// attached to several. `privacy_realm_id` is only the legacy primary pointer;
// it is used solely for a legacy grant that has no attachments at all, the
// same fallback the authorization checker makes.
import api from '../../services/api';
import { plural } from '../../nucleus/format';

export const KINDS = [
    { id: 'app', word: 'Apps', one: 'App' },
    { id: 'person', word: 'People', one: 'Person' },
    { id: 'group', word: 'Groups', one: 'Group' },
    { id: 'delegate', word: 'Agents', one: 'Agent' },
    // a COSM agent: its own account, given a realm in COSM (not an app's agent)
    { id: 'cosm_agent', word: 'COSM agents', one: 'COSM agent' },
    { id: 'automation', word: 'Automations', one: 'Automation' },
    { id: 'plugin', word: 'Plugins', one: 'Plugin' },
    { id: 'public', word: 'Public', one: 'Public page' },
    // a group's side of one membership: what that member can do in it
    { id: 'member', word: 'Members', one: 'Member' },
    // a credential someone made for a script or tool of their own: one realm, its
    // node verbs, an expiry (routes/access_tokens.py)
    { id: 'token', word: 'Tokens', one: 'Token' },
];
export const kindOne = (k) => KINDS.find((x) => x.id === k)?.one || 'Someone';

// the node verbs the backend has (authz/access.py), in the consent page's order
export const WRITES = ['create', 'edit', 'delete'];
const VERB_ORDER = ['read', 'edit', 'create', 'delete', 'propose'];
const ACCENT = '#8fa376';

export function kindOf(entityType, subKey, isToken = false) {
    if (subKey) return 'delegate';
    // a token's grant is an app grant underneath (so it rides the app path); the
    // server says when an app row is a token's own
    if (isToken) return 'token';
    if (entityType === 'app') return 'app';
    if (entityType === 'friend' || entityType === 'user') return 'person';
    // each its own kind: filed under Group they read as an unnamed "Someone · Group"
    if (entityType === 'automation') return 'automation';
    if (entityType === 'plugin_execution' || entityType === 'plugin_delegation') return 'plugin';
    if (entityType === 'public') return 'public';
    if (entityType === 'group_member') return 'member';
    if (entityType === 'cosm_agent') return 'cosm_agent';
    return 'group'; // group, group_connection
}

const titleCase = (s) => String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** {resource: {verb: {scope}}} → one line per resource, the scope in words. */
export function reachOf(blob, schemaNames = {}) {
    if (!blob || typeof blob !== 'object') return [];
    return Object.keys(blob).sort().map((res) => {
        const verbs = blob[res];
        if (!verbs || typeof verbs !== 'object' || Array.isArray(verbs) || !Object.keys(verbs).length) return null;
        const schemas = new Set();
        let nodes = 0;
        for (const s of Object.values(verbs)) {
            const scope = s && typeof s === 'object' ? s : {};
            if (Array.isArray(scope.schemas)) scope.schemas.forEach((x) => schemas.add(x));
            else if (scope.schemas && typeof scope.schemas === 'object') Object.keys(scope.schemas).forEach((x) => schemas.add(x));
            if (Array.isArray(scope.nodes)) nodes += scope.nodes.length;
        }
        let scope = null;
        if (schemas.size) {
            const names = [...schemas].map((id) => schemaNames[id] || id).sort();
            scope = names.length <= 3 ? names.join(', ') : `${names.length} schemas`;
        } else if (nodes > 0) scope = plural(nodes, 'node');
        const rank = (v) => (VERB_ORDER.indexOf(v) < 0 ? 9 : VERB_ORDER.indexOf(v));
        const vs = Object.keys(verbs).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
        return { resource: titleCase(res), verbs: vs, scope, writes: vs.some((v) => WRITES.includes(v)) };
    }).filter(Boolean);
}

/** The DEFAULT realms, which nobody may reshape: Full Access (for apps that ask for
 *  everything) and No Access (for apps that ask for nothing). Shown and duplicated,
 *  never edited or deleted (prod's isSystemRealm). */
export function isSystemRealm(j) {
    if (!j) return false;
    if (j.is_default || j.is_null_realm) return true;
    const n = String(j.name || '').toLowerCase().trim();
    return n === 'no access' || n === 'full access';
}

// THREE KINDS OF REALM. Your own, reusable: add any of them to anything. JUST FOR ONE
// (a "shared realm" in the backend): made when you share something with one person or
// app that none of your realms cover, keyed to that one recipient, never added to anyone
// else (policy._foreign_pair_realm). And the two DEFAULT realms. A shared realm with no
// recipient (an old duplicate) is an ordinary reusable realm.
export function realmOf(j, schemaNames, { inGroup = false } = {}) {
    if (!j?.id) return null;
    const justFor = !!(j.is_shared_realm && j.shared_with_id);
    return {
        id: String(j.id),
        name: j.name || 'Realm',
        description: j.description || '',
        color: j.color || ACCENT,
        is_default: !!j.is_default,
        auto_named: !!j.auto_named,
        shared: justFor,
        recipientId: justFor ? String(j.shared_with_id) : null,
        recipientType: justFor ? j.shared_with_type || null : null,
        nullRealm: !!j.is_null_realm,
        // a group's two built-in realms: "No access" (grants nothing) and "Full
        // Access", where its admins sit — the group's default realm (`is_default`),
        // still the DEFAULT apps that ask for everything get. Neither is edited or
        // deleted. Every other realm, the starter "Members" included, is ordinary.
        builtin: j.builtin === 'no_access' ? j.builtin
            : inGroup && (j.kind === 'full_access' || j.is_default) ? 'full_access' : null,
        system: !j.builtin && isSystemRealm(j),
        profileNodeId: j.profile_node_id || null,
        excludedNodeIds: Array.isArray(j.excluded_node_ids) ? j.excluded_node_ids : null,
        ceilingBlob: j.ceiling && typeof j.ceiling === 'object' ? j.ceiling : {},
        ceiling: reachOf(j.ceiling, schemaNames),
    };
}

/** One grant. Revoked and lapsed grants are kept (the who-list filters them)
 *  so the "Revoked" reading can show them; `live` says whether it grants anything. */
// What a grant is called when the server could not name it (its automation or
// plugin since deleted): what it is, never "Someone".
const UNNAMED = { automation: 'An automation', plugin: 'A plugin', public: 'Public page' };

export function profileOf(j, schemaNames, realmsById = {}) {
    if (!j?.id) return null;
    // Its OWN sub-entity key only. A base app grant lists its agents' keys in
    // `sub_entity_keys`; reading the first as its own filed the app's contract
    // as that agent, so every link to the app's contract found nothing.
    const subKey = j.sub_entity_key || null;
    const kind = kindOf(j.entity_type || 'app', subKey, !!j.access_token);
    const realmId = j.privacy_realm_id || j.privacy_realm?.id || '';
    const realm = j.privacy_realm || realmsById[realmId] || {};
    const name = j.entity_label || j.entity_name || null;
    const blob = j.composed || j.consent || {};
    const reach = reachOf(blob, schemaNames);
    const attached = (Array.isArray(j.realms) ? j.realms : []).filter((r) => r && r.id).map((r) => ({
        id: String(r.id),
        name: r.name || realmsById[r.id]?.name || 'Realm',
        color: r.color || realmsById[r.id]?.color || ACCENT,
        profileNodeId: r.profile_node_id || null,
    }));
    const realms = attached.length ? attached
        : realmId ? [{ id: String(realmId), name: realm.name || 'Realm', color: realm.color || ACCENT, profileNodeId: j.realm_profile_node_id || null }] : [];
    const revoked = j.revoked_at ? new Date(j.revoked_at) : null;
    const expires = j.expires_at ? new Date(j.expires_at) : null;
    const p = {
        id: j.id,
        kind,
        entityType: j.entity_type || 'app',
        entityId: j.entity_id || j.id,
        label: name ? (subKey ? `${subKey} · ${name}` : name) : (subKey || UNNAMED[kind] || 'Someone'),
        clientId: j.entity_client_id || null,
        subKey,
        realmId: realmId ? String(realmId) : '',
        realmName: realm.name || 'Realm',
        realmColor: realm.color || ACCENT,
        realms,
        realmIds: realms.map((r) => r.id),
        face: j.identity?.name || j.identity?.display_name || null,
        faceSource: j.identity?.source || null,
        showsAll: !!(j.identity?.shows_all || j.shows_all_identities),
        faceId: j.identity?.node_id || j.profile_node_id || null,
        chosenFaceId: j.profile_node_id || null,
        active: j.is_active !== false,
        // a friend request's offer, filed paused until they answer (not a pause of yours)
        waiting: !!j.waiting_for_answer,
        created: j.created_at ? new Date(j.created_at) : null,
        expires,
        revoked,
        blob,
        // what it consented to, before its realms narrow it: an app's tools are given here (plugin-words.md, "The rule")
        consent: j.consent || null,
        reach,
        // the access token this grant is, when it is one: revoked as a token, and
        // who made it, when it was last used
        token: j.access_token ? {
            id: String(j.access_token.id),
            madeBy: j.access_token.made_by?.username || null,
            lastUsed: j.access_token.last_used_at ? new Date(j.access_token.last_used_at) : null,
        } : null,
    };
    p.expired = !!(expires && expires.getTime() < Date.now());
    p.live = !revoked && !p.expired;
    p.key = `${kind}:${p.entityId}:${subKey || ''}`;
    p.unbounded = reach.some((r) => r.resource === 'Nodes' && !r.scope && r.writes);
    p.summary = reach.length
        ? reach.map((r) => `${r.verbs.join(', ')} ${r.resource.toLowerCase()}${r.scope ? ` · ${r.scope}` : ''}`).join('; ')
        : 'No reach';
    return p;
}

/** Everyone attached to a realm — through any of the realms they draw on, not
 *  the primary pointer alone. Revoked and lapsed grants reach nothing. */
export function attachedTo(profiles, realmId) {
    return profiles.filter((p) => p.live && p.realmIds.includes(String(realmId)));
}

// A plugin run's own grant: made paused, never shown as a contract, and not a use
// of the realm (the server revokes it with the realm).
const RUN_GRANTS = new Set(['plugin_execution', 'plugin_delegation']);

/** What keeps a realm from being deleted: every grant on it not revoked. A lapsed
 *  one reaches nothing but can be extended, so the server counts it too; a paused
 *  plugin-run grant it does not. */
export function holdingRealm(profiles, realmId) {
    return profiles.filter((p) => !p.revoked && p.realmIds.includes(String(realmId))
        && !(RUN_GRANTS.has(p.entityType) && !p.active));
}

const HISTORY_LABELS = { created: 'Created', updated: 'Updated', revoked: 'Revoked', paused: 'Paused', resumed: 'Resumed' };

/** One ledger entry → {at, title, chips:[{tone, text}]}. `change_summary` is an
 *  object (verbs, schemas and nodes added and removed), drawn as +/− chips by
 *  name; a raw id is never shown (prod's DiffChips / historyTitle). */
export function historyOf(e, schemaNames = {}, entityType) {
    const at = e.created_at || e.at || e.timestamp;
    const obj = e.change_summary && typeof e.change_summary === 'object' && !Array.isArray(e.change_summary);
    const cs = obj ? e.change_summary : {};
    const labels = cs.labels || {};
    const bare = (id) => { const l = labels[id]; return l ? (l.includes(':') ? l.split(':').slice(1).join(':') : l) : 'node'; };
    const na = cs.nodes_added || [];
    const nr = cs.nodes_removed || [];
    const others = (cs.schemas_added || []).length + (cs.schemas_removed || []).length
        + (cs.verbs_added || []).length + (cs.verbs_removed || []).length;
    const action = e.action ? String(e.action) : '';
    let title;
    if (action === 'created') title = entityType === 'friend' ? 'Friend share created' : 'Created';
    else if (action && action !== 'updated') title = HISTORY_LABELS[action] || titleCase(action);
    else if (na.length && !nr.length && !others) title = na.length === 1 ? `Shared node · ${bare(na[0])}` : `Shared ${na.length} nodes`;
    else if (nr.length && !na.length && !others) title = nr.length === 1 ? `Un-shared node · ${bare(nr[0])}` : `Un-shared ${nr.length} nodes`;
    else title = action ? 'Updated' : 'Changed';
    if (typeof e.change_summary === 'string' && e.change_summary.trim()) title = `${title} — ${e.change_summary}`;

    const chips = [];
    const schemaName = (sid) => labels[sid] || schemaNames[sid] || null;
    const named = (arr, add, labeler, noun) => {
        const list = arr || [];
        if (!list.length) return;
        const tone = add ? 'add' : 'remove';
        const sign = add ? '+' : '−';
        const names = list.map(labeler);
        if (names.every(Boolean)) {
            names.slice(0, 4).forEach((t) => chips.push({ tone, text: `${sign} ${t}` }));
            if (names.length > 4) chips.push({ tone, text: `${sign}${names.length - 4} more` });
        } else chips.push({ tone, text: `${sign}${list.length} ${noun}${list.length > 1 ? 's' : ''}` });
    };
    named(cs.schemas_added, true, schemaName, 'schema');
    named(cs.schemas_removed, false, schemaName, 'schema');
    named(na, true, (id) => labels[id] || null, 'node');
    named(nr, false, (id) => labels[id] || null, 'node');
    (cs.verbs_added || []).forEach((v) => chips.push({ tone: 'add', text: `+ ${v}` }));
    (cs.verbs_removed || []).forEach((v) => chips.push({ tone: 'remove', text: `− ${v}` }));
    if (cs.realm_changed) chips.push({ tone: 'neutral', text: 'realm changed' });
    return { at: at ? new Date(at) : null, title, chips };
}

/** The space's realms and grants: a group's own when you are in the group.
 *
 * THE THREE READS GO TOGETHER. The kinds, the realms and the grants were awaited one
 * after another, and the mapping below is what made that look necessary: `realmOf` wants
 * the schema names, `profileOf` wants the names AND the realms it has just built. But that
 * is an order on the MAPPING, not on the fetching — none of the three requests needs
 * another's answer to be sent. Serialized, the page cost three round trips of latency
 * before it could draw; on a real connection that was most of the wait, and no amount of
 * backend work would have touched it.
 *
 * `/privacy-realms/null` stays sequential, because whether to ask is the realm list's
 * answer. It is also the only one that is normally skipped.
 */
const SCHEMA_PAGE = 500;

/** Every schema the space can see, every version, a page at a time — not the first
 *  500. A failed read is an empty list (names fall back to ids), never an error. */
export async function allSchemas(groupId) {
    const out = [];
    for (let offset = 0; ; offset += SCHEMA_PAGE) {
        const r = await api.request(`/schemas?limit=${SCHEMA_PAGE}&offset=${offset}${groupId ? `&group_id=${encodeURIComponent(groupId)}` : ''}`).catch(() => null);
        const rows = (r?.schemas || []).filter((x) => x?.id);
        out.push(...rows);
        const total = typeof r?.total === 'number' ? r.total : out.length;
        if (rows.length < SCHEMA_PAGE || out.length >= total) return out;
    }
}

/** id -> what a schema is called, and id -> the schema it is a version of
 *  (schema_name: the versions of one schema share it, and only they do). */
export function schemaMaps(rows) {
    const names = {};
    const keys = {};
    for (const row of rows) {
        names[row.id] = row.display_name || row.name || row.schema_name || 'schema';
        keys[row.id] = row.schema_name || row.id;
    }
    return { names, keys };
}

export async function loadRealms(groupId, schemaNames = {}) {
    const g = groupId ? `/groups/${groupId}` : '';
    const haveNames = !!Object.keys(schemaNames).length;
    const [s, rawRealms, e] = await Promise.all([
        // A caller that already has the names (nobody today) skips the request, not the map.
        haveNames ? null : allSchemas(groupId),
        groupId ? api.getGroupPrivacyRealms(groupId) : api.getPrivacyRealms(),
        api.request(`${g}/exposure-profiles`),
    ]);

    let names = schemaNames;
    let keys = {};
    if (!haveNames) ({ names, keys } = schemaMaps(s || []));

    const list = Array.isArray(rawRealms) ? rawRealms : (rawRealms?.privacy_realms || rawRealms?.realms || []);
    // No Access is made on first ask, as the consent page asks for it; a person's list may
    // not hold it yet
    if (!groupId && !list.some((r) => r?.is_null_realm)) {
        const none = await api.request('/privacy-realms/null').catch(() => null);
        if (none?.id) list.push(none);
    }
    const realms = list.map((r) => realmOf(r, names, { inGroup: !!groupId })).filter(Boolean);
    const byId = Object.fromEntries(realms.map((r) => [r.id, r]));
    const profiles = (e?.exposure_profiles || e?.profiles || []).map((j) => profileOf(j, names, byId)).filter(Boolean);
    return { realms, profiles, schemaNames: names, schemaKeys: keys };
}
